import * as signalR from "@microsoft/signalr";
import { apiHost } from "../api/apiClient";
import _ from "lodash";

const MAX_RETRIES = 4;
const RETRY_INTERVAL = 2000;

/**
 * Single point for interacting with server using signalR
 */
class signalRHelper {

  // Connection object
  connection = null;
  connected = false;
  connectionAttemptInProgress = false;
  retries = 0;

  subscriptions = [];
  assetFilters = [];
  groupFilters = [];

  // Init
  constructor() {
    // Create connection
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiHost}/VisionTrack`, {
        accessTokenFactory: () => localStorage.getItem("token"),
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onreconnected(connectionId => {
      console.assert(this.connection.state === signalR.HubConnectionState.Connected);

      this.subscriptions.forEach((subscription) => {
        this.connection.invoke('subscribe', subscription);
      });

      this.assetFilters.forEach((asset) => {
        this.connection.invoke('addAssetFilter', asset);
      });

      this.groupFilters.forEach((group) => {
        this.connection.invoke('addGroupFilter', group);
      });
    });
  }

  /**
   * Get the connection. If not connected, connect to signalR
   * @returns Promise
   */
  async getConnection(resolve, reject) {
    if (this.connection.state === signalR.HubConnectionState.Connected) {
      // If there is a chain, attach
      if (resolve) {
        resolve(this.connection);
      }
      return Promise.resolve(this.connection);
    }

    // Check if max retries have been exceeded
    if (this.retries > MAX_RETRIES) {
      const err = new Error("Could not establish signalR connection. Max retries exceeded");
      if (reject) {
        return reject(err);
      }
      return Promise.reject(err);
    }

    // Check if we have connected once. If so, auto reconnect will kick in
    if (this.connected) {
      return new Promise((res, rej) => {
        setTimeout(() => this.getConnection(res, rej), RETRY_INTERVAL);
      });
    } else {
      // Check if another connection attempt is in progress. In such a case, poll after a timeout
      if (this.connectionAttemptInProgress) {
        return new Promise((res, rej) => {
          setTimeout(() => this.getConnection(res, rej), RETRY_INTERVAL);
        });
      }

      // Connection isn't established yet
      try {
        this.connectionAttemptInProgress = true;
        this.retries = this.retries + 1;

        await this.connection.start();

        console.assert(this.connection.state === signalR.HubConnectionState.Connected);

        this.connected = true;
        this.connectionAttemptInProgress = false;

        // Run any previous subscription invocations to keep this connection up to date, in case of loss of connection after negotiation
        this.subscriptions.forEach((subscription) => {
          this.connection.invoke('subscribe', subscription);
        });

        this.assetFilters.forEach((asset) => {
          this.connection.invoke('addAssetFilter', asset);
        });

        this.groupFilters.forEach((group) => {
          this.connection.invoke('addGroupFilter', group);
        });

        // If there is a chain, attach
        if (resolve) {
          resolve(this.connection);
        }

        return Promise.resolve(this.connection);
      } catch (err) {
        console.assert(this.connection.state === signalR.HubConnectionState.Disconnected);
        console.log(err);

        // Retry
        return new Promise((res, rej) => {
          setTimeout(() => this.getConnection(res, rej), RETRY_INTERVAL);
        });
      }
    }
  }

  unsubscribe(endpoint) {
    this.subscriptions = _.without(this.subscriptions, endpoint);

    if (this.connection.state === signalR.HubConnectionState.Connected) {
      this.connection.invoke('unsubscribe', endpoint);
    }
  }

  subscribe(endpoint) {
    this.subscriptions.push(endpoint);

    if (this.connection.state === signalR.HubConnectionState.Connected) {
      this.connection.invoke('subscribe', endpoint);
    }
  }

  setAssetFilter(asset) {
    this.assetFilters = [asset];

    if (this.connection.state === signalR.HubConnectionState.Connected) {
      this.connection.invoke('resetAssetFilter');
      this.connection.invoke('addAssetFilter', asset);
    }
  }

  resetAssetFilter() {
    this.assetFilters = [];

    if (this.connection.state === signalR.HubConnectionState.Connected) {
      this.connection.invoke('resetAssetFilter');
    }
  }

  setGroupFilter(group) {
    this.groupFilters = [group];

    if (this.connection.state === signalR.HubConnectionState.Connected) {
      this.connection.invoke('resetGroupFilter');
      this.connection.invoke('addGroupFilter', group);
    }
  }

  resetGroupFilter() {
    this.groupFilters = [];

    if (this.connection.state === signalR.HubConnectionState.Connected) {
      this.connection.invoke('resetGroupFilter');
    }
  }

}

// Create and export single instance
const signalRHelperInstance = new signalRHelper();
export default signalRHelperInstance;
